import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PROJECT_NAME } from "../../../config";
import { REAL_ESTATE_FIXED_SIZES } from "../../../scenes/map/mapMarkerSizes";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import ModalFrame from "../../primitives/ModalFrame";
import posthog from 'posthog-js';


interface MapOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStepChange?: (step: number) => void;
}

interface FloatingPlus {
  id: number;
  y: number;
  opacity: number;
  value: number; // Значення для відображення (+1 або +1.2)
}


export default function MapOnboardingModal({ isOpen, onClose, onStepChange }: MapOnboardingModalProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [energyAnimation, setEnergyAnimation] = useState(100);
  const [floatingPluses, setFloatingPluses] = useState<FloatingPlus[]>([]);
  const [pearlBalance, setPearlBalance] = useState(0);
  const [shellScale, setShellScale] = useState(1);
  const plusIdCounter = useRef(0);
  const energyAnimationRef = useRef(100);
  const isPausedRef = useRef(false);
  const isFillingRef = useRef(false);

  // (b) Reset demo energy UI when leaving step 0 or closing — during render
  const energyDemoActive = currentStep === 0 && isOpen;
  const [prevEnergyDemoActive, setPrevEnergyDemoActive] = useState(energyDemoActive);
  if (energyDemoActive !== prevEnergyDemoActive) {
    setPrevEnergyDemoActive(energyDemoActive);
    if (!energyDemoActive) {
      setEnergyAnimation(100);
      setPearlBalance(0);
      setFloatingPluses([]);
    }
  }

  // (c) Timer-driven energy demo on step 0 — setState only in interval callbacks
  useEffect(() => {
    if (currentStep === 0 && isOpen) {
      // Анімація зменшення енергії
      let energy = 100;
      energyAnimationRef.current = 100;
      isPausedRef.current = false;
      isFillingRef.current = false;
      const interval = setInterval(() => {
        // Пропускаємо оновлення під час паузи або заповнення
        if (isPausedRef.current || isFillingRef.current) return;
        
        // Завжди читаємо поточне значення з ref (синхронізація після заповнення)
        energy = energyAnimationRef.current;
        
        // Різна швидкість зменшення залежно від рівня енергії
        if (energy > 30) {
          // Зелений діапазон - швидше зменшення
          energy -= 1.5;
        } else if (energy > 15) {
          // Жовтий/помаранчевий діапазон - середня швидкість
          energy -= 1.2;
        } else if (energy > 0) {
          // Червоний діапазон - повільніше зменшення (довше показуємо червоний)
          energy -= 0.8;
        }
        
        if (energy <= 0) {
          energy = 0;
          isPausedRef.current = true;
          // Пауза 2 секунди коли енергія = 0 (довше показуємо відсутність енергії)
          setTimeout(() => {
            // Починаємо анімацію заповнення енергії через тапи
            isFillingRef.current = true;
            const tapCount = 3; // 3 тапи для заповнення
            const energyPerTap = 100 / tapCount; // ~33.33% за тап
            
            const performTap = (tapIndex: number) => {
              if (tapIndex >= tapCount) {
                const finalEnergy = 100;
                setEnergyAnimation(finalEnergy);
                energyAnimationRef.current = finalEnergy;
                // Локальна змінна energy оновиться автоматично на наступній ітерації setInterval
                isPausedRef.current = false;
                isFillingRef.current = false;
                return;
              }
              
              // Анімація scale мюшлі (як на TapScreen)
              setShellScale(0.9);
              setTimeout(() => {
                setShellScale(1);
              }, 150);
              
              // Збільшуємо енергію на третину
              const currentEnergy = (tapIndex + 1) * energyPerTap;
              setEnergyAnimation(currentEnergy);
              energyAnimationRef.current = currentEnergy;
              
              // Наступний тап через 300ms
              setTimeout(() => {
                performTap(tapIndex + 1);
              }, 300);
            };
            
            // Починаємо перший тап
            performTap(0);
          }, 2000);
        }
        
        setEnergyAnimation(energy);
        energyAnimationRef.current = energy;
      }, 80); // Трохи швидше для плавнішої анімації
      
      // Окремий інтервал для створення floatingPlus та збільшення балансу - рівно раз на секунду
      const plusInterval = setInterval(() => {
        // Пропускаємо створення під час паузи або заповнення
        if (isPausedRef.current || isFillingRef.current) return;
        
        // Перевіряємо поточну енергію з ref
        const currentEnergy = energyAnimationRef.current;
        
        // Створюємо новий floatingPlus та збільшуємо баланс тільки якщо є енергія
        if (currentEnergy > 15) {
          const newId = plusIdCounter.current++;
          setFloatingPluses([{ id: newId, y: 0, opacity: 0, value: 1 }]);
          // Збільшуємо баланс рівно на 1 раз на секунду (як на TapScreen)
          setPearlBalance((prev) => prev + 1);
        } else {
          // Якщо енергія закінчилася, очищаємо
          setFloatingPluses([]);
        }
      }, 1000); // Рівно раз на секунду
      
      return () => {
        clearInterval(interval);
        clearInterval(plusInterval);
        energyAnimationRef.current = 100;
      };
    }
  }, [currentStep, isOpen]);

  // Анімація "+1" - моргає раз на секунду (не дуже швидко)
  useEffect(() => {
    if (floatingPluses.length === 0) return;

    const animationInterval = setInterval(() => {
      setFloatingPluses((prev) =>
        prev
          .map((plus) => {
            // Моргає: з'являється (opacity 0 -> 1), потім зникає (opacity 1 -> 0)
            const newOpacity =
              plus.opacity < 1
                ? Math.min(1, plus.opacity + 0.08) // Фаза з'явлення: ~200ms
                : Math.max(0, plus.opacity - 0.02); // Фаза зникнення: ~800ms
            return {
              ...plus,
              opacity: newOpacity,
            };
          })
          .filter((plus) => plus.opacity > 0)
      );
    }, 16); // ~60fps

    return () => clearInterval(animationInterval);
  }, [floatingPluses.length]);

  // Повідомляємо про зміну кроку при відкритті модалки
  useEffect(() => {
    if (isOpen) {
      onStepChange?.(currentStep);
    }
  }, [isOpen, currentStep, onStepChange]);

  if (!isOpen) return null;

  const steps = [
    // Крок 0: Початок покладено
    {
      title: t("Beginning Laid"),
      content: (
        <div className="flex flex-col items-center gap-md text-center">
          <div className="[font-family:Lato,sans-serif] text-[15px] leading-[1.4] font-light text-white">
            {t("Great! You clicked on the shell with pearl and charged it with energy.")}
          </div>
          <div className="[font-family:Lato,sans-serif] text-[15px] leading-[1.4] font-light text-white">
            {t("Keep your energy supply maintained regularly, otherwise collecting pearls (the main currency of {{projectName}}) will be paused.", {
              projectName: PROJECT_NAME,
            })}
          </div>
        </div>
      ),
      showEnergyAnimation: true,
    },
    // Крок 1: Про нерухомість
    {
      title: t("Real Estate"),
      content: (
        <div className="flex flex-col items-center gap-md text-center">
          <div className="flex flex-wrap items-center justify-center gap-sm [font-family:Lato,sans-serif] text-[15px] leading-[1.4] font-light text-white">
            {t("Work hard to accumulate lots of Pearls to purchase your first piece of real estate.")}
            <img src="/ui/shell-icon.png" alt="Shell" className="h-7 w-7 align-middle object-contain" />
          </div>
          <div className="mt-md grid w-full max-w-[240px] grid-cols-3 gap-[-2px]">
            {[2, 4, 6].map((num) => {
              const size = REAL_ESTATE_FIXED_SIZES[num];
              return (
                <img
                  key={num}
                  src={`/game-assets/map-objects/real-estate/${num}.webp`}
                  alt={`Real Estate ${num}`}
                  className="object-contain"
                  style={{ 
                    width: `${size.width / 1.5}px`,
                    height: `${size.height / 1.5}px`,
                  }}
                />
              );
            })}
          </div>
          <div className="mt-md flex flex-wrap items-center justify-center gap-sm [font-family:Lato,sans-serif] text-[15px] leading-[1.4] font-light text-white">
            {t("To buy real estate, just click on the icon:")}
            <img src="/game-assets/map-objects/plot.webp" alt="Free Plot" className="h-7 w-7 align-middle object-contain" />
          </div>
          <div className="[font-family:Lato,sans-serif] text-[15px] leading-[1.4] font-light text-white">
            {t("Hurry up — their number is very limited!")}
          </div>
        </div>
      ),
    },
    // Крок 2: Про VIP
    {
      title: t("VIP"),
      content: (
        <div className="flex flex-col items-center gap-md text-center">
          <div className="[font-family:Lato,sans-serif] text-[15px] leading-[1.4] font-light text-white">
            {t("You can also purchase")} {t("VIP")} {t("to get Pearls faster and be among the first to purchase real estate!")}
          </div>
          <img
            src="/ui/item-icons/1.png"
            alt="VIP"
            className="mt-md h-[100px] w-[100px] object-contain"
          />
        </div>
      ),
    },
    // Крок 3: Про щоденні квести
    {
      title: t("Daily Quests"),
      content: (
        <div className="flex flex-col items-center gap-md text-center">
          <div className="[font-family:Lato,sans-serif] text-[15px] leading-[1.4] font-light text-white">
            {t("Complete")} {t("Daily Quests")} {t("and get daily rewards for logging into the game!")}
          </div>
          <div className="relative mt-md flex items-center justify-center">
            {/* Кнопка квестів з мапи з маркером (коли є доступні квести) - точно як на мапі */}
            <div
              className="pointer-events-auto relative flex h-[53px] w-[53px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-onboarding-action shadow-[inset_5px_4px_8px_0px_var(--color-black-alpha-12)]"
            >
              <img
                src="/ui/daily-quests/daily-quests-map-button-icon.svg"
                alt="Daily Quests"
                className="h-[30px] w-[30px] object-contain"
              />
              {/* Exclamation mark badge - низ зображення на вертикальному центрі кнопки, максимально вліво */}
              <img
                src="/ui/world-map/quest-main-marker-active.png"
                alt="!"
                className="pointer-events-none absolute bottom-1/2 -left-[15px] h-10 w-10 object-contain"
              />
            </div>
          </div>
        </div>
      ),
    },
    // Крок 4: Про вибори мера
    {
      title: t("Mayor Elections"),
      content: (
        <div className="flex flex-col items-center gap-md text-center">
          <div className="[font-family:Lato,sans-serif] text-[15px] leading-[1.4] font-light text-white">
            {t("You can also register as a candidate for")} {t("Mayor Elections")} {t("to forever enter the history of")} {PROJECT_NAME}!
          </div>
          <img
            src="/game-assets/map-objects/cityhall.webp"
            alt="City Hall"
            className="mt-md h-[100px] w-[100px] object-contain"
          />
        </div>
      ),
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      onStepChange?.(newStep);
      posthog.capture('onboarding_map_tutorial_step', { step: newStep + 1, total_steps: steps.length });
    } else {
      onClose();
    }
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const stepNumber = currentStep + 1;
  const totalSteps = steps.length;
  const titleWithStep = `${currentStepData.title} (${stepNumber}/${totalSteps})`;

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={isLastStep ? onClose : () => {}}
      closeOnBackdropClick={isLastStep}
      variant="dialog"
      className="max-w-[400px] rounded-xl border-[3px] border-deep-blue"
      renderHeader={() => (
        <div className="px-2xl pt-2xl pb-xl text-center [font-family:'Rubik_One',sans-serif] text-[16px] leading-[1.3] font-normal text-white">
          {titleWithStep}
        </div>
      )}
    >
        {/* Кнопка - в самому верху під хедером */}
        <button
          onClick={handleNext}
          className="mb-3xl w-full cursor-pointer rounded-pill border-none bg-brand px-3xl py-lg [font-family:'Rubik_One',sans-serif] text-[16px] font-normal text-white [transition:background_0.2s]"
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--color-map-onboarding-modal-surface)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--color-brand)';
          }}
        >
          {isLastStep ? t("Start Your Story") : t("Next Step")}
        </button>

        {/* Баланс мюшлей зверху (тільки для кроку 0) */}
        {currentStep === 0 && (
          <div className="mb-xl flex flex-col items-center justify-center gap-xs">
            {/* Анімація "(+1)" над балансом, золотого кольору - показуємо тільки останній */}
            {/* Завжди резервуємо місце для "+1", щоб уникнути layout shift */}
            <div className="flex h-5 items-center justify-center">
              {floatingPluses.length > 0 && (
                <span
                  className="[font-family:Lato,sans-serif] text-[14px] leading-[20px] font-bold tracking-[-0.747px] text-gold [transition:none]"
                  style={{
                    opacity: floatingPluses[floatingPluses.length - 1].opacity,
                  }}
                >
                  +1
                </span>
              )}
            </div>
            <div className="flex items-center justify-center gap-md">
              <span className="[font-family:Lato,sans-serif] text-[18px] font-bold text-white">
                {Math.floor(pearlBalance)}
              </span>
              <img
                src="/ui/tap-screen/pearl-icon.png"
                alt="Pearl"
                className="h-6 w-6 object-contain"
              />
            </div>
          </div>
        )}

        {/* Контент */}
        <div className="mb-3xl w-full">
          {currentStep === 0 && currentStepData.showEnergyAnimation ? (
            <div className="flex flex-col items-center gap-2xl">
              {/* Анімація мюшлі з енергією */}
              <div className="relative h-[200px] w-[200px]">
                <CircularProgressbar
                  value={energyAnimation}
                  strokeWidth={10}
                  styles={buildStyles({
                    pathColor: (() => {
                      // Плавна зміна кольору від зеленого до червоного
                      if (energyAnimation > 30) {
                        return 'var(--color-brand)'; // Зелений
                      } else if (energyAnimation > 15) {
                        // Перехід від зеленого до жовтого/помаранчевого
                        const ratio = (energyAnimation - 15) / 15;
                        const r = Math.floor(55 + (255 - 55) * (1 - ratio));
                        const g = Math.floor(191 - 100 * (1 - ratio));
                        const b = Math.floor(164 - 164 * (1 - ratio));
                        return `rgb(${r}, ${g}, ${b})`;
                      } else {
                        // Червоний - показується довше (від 30% до 0%)
                        return "var(--color-energy-critical)"; // Червоний
                      }
                    })(),
                    trailColor: "var(--color-progress-trail)",
                    strokeLinecap: "round",
                  })}
                />
                <div
                  className="absolute top-1/2 left-1/2 flex h-[150px] w-[150px] items-center justify-center [transition:transform_0.15s_ease-out]"
                  style={{
                    transform: `translate(-50%, -50%) scale(${shellScale})`,
                  }}
                >
                  <img
                    src="/ui/tap-screen/energy-shell.png"
                    alt="Shell"
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
              {currentStepData.content}
            </div>
          ) : (
            currentStepData.content
          )}
        </div>
    </ModalFrame>
  );
}
